// FlixSubtitleHandler.jsx
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import getCurrentLanguage from '../resource/getCurrentLanguage';
import getResourceUrl from '../resource/getResourceUrl';

export const FlixSubtitleHandler = ({
  messageId,
  player,
  isPlayingSplash,
  isSubtitleActive,
  isVideoReloaded,
  setIsVideoReloaded,
}) => {
  const currentSegmentIdx = useRef(1);
  const timeupdateHandlerRef = useRef(null);
  const { t } = useTranslation();

  /*
   * load webvtt file
   */
  useEffect(() => {
    if (isPlayingSplash || !isSubtitleActive || !player) return;

    player.ready(() => {
      const WebVTT = window?.WebVTT;
      if (!WebVTT) return;

      const loadedSegmentIdx = new Set([]);
      const DURATION = 100;

      const currentLanguage = getCurrentLanguage();
      let lang = currentLanguage;
      if (lang.startsWith('zh')) {
        lang =
          lang === 'zh-TW' ? 'zh-hant' : lang === 'zh-CN' ? 'zh' : 'zh-hant';
      }
      const webVTTSrc = getResourceUrl({
        endpoint: `/messages/${messageId}/subtitles/${lang}.vtt`,
      });

      // add text track the first time or when the flix is reloaded.
      // (`undefined` indicates first time load;
      // `true` means reloaded
      // `false` means reset after first load or reloaded)
      if (typeof isVideoReloaded === 'undefined' || isVideoReloaded === true) {
        const currentTime = player.currentTime();
        const segmentIdx = Math.floor(currentTime / DURATION) + 1;

        const textTracks = player.getMediaElement().textTracks;
        if (textTracks?.length) {
          const textTrack = textTracks[textTracks.length - 1];

          // Show subtitles before remove them; otherwise Safari gets no cues to get rid of.
          textTrack.mode = 'showing';

          // Remove all the cues; otherwise Chrome's behavior would be different from Safari's
          // (Can't get cues after video reload under Chrome;
          // or it would get double cues if forcing to reload vtt file again)
          if (textTrack.cues?.length) {
            Object.values(textTrack.cues).forEach(cue =>
              textTrack.removeCue(cue)
            );
          }
        }

        loadWebVTT(segmentIdx);

        loadedSegmentIdx.clear();
        loadedSegmentIdx.add(segmentIdx);

        currentSegmentIdx.current = segmentIdx;

        player.off('timeupdate', timeupdateHandlerRef.current);

        // reset it for later reuse
        setIsVideoReloaded(false);

        // The new `handleTimeupdate` would be created every time when the video is reloaded.
        // Which means we have to keep it for next time use (in our case: to release correct handlers)
        timeupdateHandlerRef.current = handleTimeupdate;
        player.on('timeupdate', handleTimeupdate);
      }

      // The function to add cues to text track
      async function loadWebVTT(idx) {
        webVTTSrc.searchParams.set('segment', idx);
        webVTTSrc.searchParams.set('duration', DURATION);
        const src = webVTTSrc.href;
        const res = await fetch(src);
        const webvtt = await res.text();

        const parser = new WebVTT.Parser(window, WebVTT.StringDecoder());
        parser.oncue = function (cue) {
          const textTracks = player.getMediaElement().textTracks;
          textTracks[textTracks.length - 1].addCue(cue);
        };
        parser.parse(webvtt);
        parser.flush();
      }

      function handleTimeupdate() {
        const currentTime = player.currentTime();
        const segmentIdx = Math.floor(currentTime / DURATION) + 1;
        // To prevent timeupdate event from being triggered frequently
        if (segmentIdx !== currentSegmentIdx.current) {
          currentSegmentIdx.current = segmentIdx;
          if (loadedSegmentIdx.has(segmentIdx)) return;

          loadedSegmentIdx.add(segmentIdx);
          loadWebVTT(segmentIdx);
        }
      }
    });
  }, [
    t,
    messageId,
    player,
    isPlayingSplash,
    isVideoReloaded,
    setIsVideoReloaded,
    isSubtitleActive,
  ]);

  useEffect(() => {
    return () => {
      if (player && timeupdateHandlerRef.current) {
        player?.off?.('timeupdate', timeupdateHandlerRef.current);
      }
    };
  }, [player]);

  /*
   * on/off subtitle
   */
  useEffect(() => {
    if (!player?.getMediaElement?.().textTracks?.length) return;

    player.ready(() => {
      const textTracks = player.getMediaElement().textTracks;
      textTracks[textTracks.length - 1].mode = isSubtitleActive
        ? 'showing'
        : 'disabled';
    });
  }, [player, isSubtitleActive]);

  /*
   * change active cue's position when palyer control is opend or closed.
   */
  // TODO
  // Shaka doesn't have a custom subtitle layer.
  // Have to figure out a new way to adjust cue position.
  /*
  useEffect(() => {
    if (isPlayingSplash || !player) return;

    const handleCuePosition = () => {
      player.textTrackSettings?.updateDisplay();

      const activeCues = textTrackRef.current?.activeCues;
      if (!activeCues?.length) return;

      const activeCueElement = document.querySelector(
        '.vjs-text-track-cue > div'
      );
      if (!activeCueElement) return;

      const SAFE_AREA_INSET_TOP = 34;
      // For bottom bar on IOS safari.
      if (isFullscreen && getIsIphoneXSeries()) {
        const fontSize = parseInt(
          activeCueElement.parentElement.style.fontSize
        );

        if (fontSize > 16) {
          const innerHeight = window.innerHeight;
          activeCueElement.parentElement.style.top = `${
            innerHeight - SAFE_AREA_INSET_TOP * 2
          }px`;
        }
        activeCueElement.style.top = isControlOpened
          ? `${-(SAFE_AREA_INSET_TOP * 2)}px`
          : `${-SAFE_AREA_INSET_TOP}px`;
        activeCueElement.parentElement.style.fontSize = '16px';
      } else {
        activeCueElement.style.top = isControlOpened
          ? `${-SAFE_AREA_INSET_TOP}px`
          : '0px';
      }
    };

    player.ready(() => {
      handleCuePosition();
      textTrackRef.current?.addEventListener('cuechange', handleCuePosition);
      window.addEventListener('resize', handleCuePosition);
    });
    return () => {
      textTrackRef.current?.removeEventListener('cuechange', handleCuePosition);
      window.removeEventListener('resize', handleCuePosition);
    };
  }, [
    isControlOpened,
    player,
    isPlayingSplash,
    isFullscreen,
    isVideoReloaded,
    textTrackRef.current?.mode,
  ]);
  */

  return null;
};

FlixSubtitleHandler.propTypes = {
  messageId: PropTypes.string,
  player: PropTypes.object,
  isPlayingSplash: PropTypes.bool,
  isSubtitleActive: PropTypes.bool,
  isVideoReloaded: PropTypes.bool,
  setIsVideoReloaded: PropTypes.func,
};

FlixSubtitleHandler.defaultProps = {
  messageId: '',
  isPlayingSplash: true,
  isSubtitleActive: false,
  isControlOpened: false,
  // Explicitly specify isVideoReloaded to undefined
  // to indicate that it's never reloaded yet.
  // Otherwise, it should be true (reloaded) or false (reset)
  isVideoReloaded: undefined,
  isFullscreen: false,
  setIsVideoReloaded: () => null,
};

export default FlixSubtitleHandler;
